import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EntitiesTableList } from '../../../components';
import { AlertModal } from '../../../components/modals/AlertModal/AlertModal';
import { fetchHealthOrganizations, disableHO, clearHOInfo } from '../../../store/health_organizations';
import { resendInvitationForUser, sendNewPassword, loginAsDifferentRole } from '../../../store/authorization';
import { renderColumns, transformData } from './renderColumns';
import { URLS } from '../../../constants/constants';
import { redirectToPage } from '../../../utils/helpers';
import { usePagination } from '../../../hooks/usePagination';
import { useSearch } from '../../../hooks/useSearch';
import { Navigation } from '../../../utils/navigation';

const Child_Health_Organizations = ({
  disableHO,
  fetchHealthOrganizations,
  limit,
  currentPage,
  total,
  totalPages,
  healthOrganizations,
  sendNewPassword,
  resendInvitationForUser,
  clearHOInfo,
  loginAsDifferentRole,
  isViewDashboardMode,
  currentHealthOrganization,
}) => {
  const hasFetchedOrganizationsRef = useRef(false);
  const navigate = Navigation();
  const [selectedOrganizationStatus, setSelectedOrganizationStatus] = useState(
    () => new URLSearchParams(navigate?.location?.search).get('status') || 'ACTIVE',
  );
  usePagination(navigate);
  const { searchValue, highlighted, handleChangeSearch } = useSearch(
    fetchHealthOrganizations,
    () => ({ organizationStatus: selectedOrganizationStatus }),
  );

  useEffect(() => {
    if (hasFetchedOrganizationsRef.current) return;
    hasFetchedOrganizationsRef.current = true;
    fetchHealthOrganizations({ Navigation: navigate, organizationStatus: selectedOrganizationStatus });
    return;
  }, [fetchHealthOrganizations, navigate]);

  useEffect(() => () => { clearHOInfo(); return; }, [clearHOInfo]);

  const handleManageOrganization = useCallback(
    (id) => () => navigate.push(`/health_organizations/${id}/edit`),
    [navigate],
  );

  const switchOrganizationStatus = useCallback((status) => () => disableHO({ ...status, Navigation: navigate }), [disableHO, navigate]);

  const handleSendPassword = useCallback((id) => () => sendNewPassword(id), [sendNewPassword]);

  const handleResendInvitation = useCallback((id) => () => resendInvitationForUser(id), [resendInvitationForUser]);

  const handleOrganizationStatus = (value) => {
    setSelectedOrganizationStatus(value);
    redirectToPage(URLS.HEALTH_ORGANIZATIONS, 1, navigate, { status: value });
    fetchHealthOrganizations({ value: searchValue, organizationStatus: value, filtered: true, Navigation: navigate });
  };

  const handleChangePage = (page) => {
    redirectToPage(URLS.HEALTH_ORGANIZATIONS, page, navigate, { status: selectedOrganizationStatus });
    fetchHealthOrganizations({ value: searchValue, Navigation: navigate, organizationStatus: selectedOrganizationStatus });
  };

  const handleViewDashboard = useCallback(
    (viewDashboardParams) => () => loginAsDifferentRole({ ...viewDashboardParams, Navigation: navigate }),
    [loginAsDifferentRole, navigate],
  );

  const parentHasActiveAdmin = Boolean(currentHealthOrganization?.admin) && !currentHealthOrganization?.disabled;

  const [blockedFacilityName, setBlockedFacilityName] = useState('');
  const { alertModalVisible, setAlertModalVisible, Modal: EnterpriseAdminAlertModal } = AlertModal({
    header: 'Enterprise Admin Inactive',
    message: `${blockedFacilityName || 'This facility'} can't be activated because your Enterprise Admin is not active. Please activate the Enterprise Admin first, then try again.`,
    buttonText: 'Got it',
  });

  const onBlockActivation = useCallback((record) => {
    setBlockedFacilityName(record?.name);
    setAlertModalVisible(true);
  }, [setAlertModalVisible]);

  const tableData = useMemo(() => {
    return transformData({
      data: healthOrganizations,
      handleManageOrganization,
      switchOrganizationStatus,
      handleSendPassword,
      handleResendInvitation,
      handleViewDashboard,
      isViewDashboardMode,
      parentHasActiveAdmin,
      onBlockActivation,
    });
  }, [
    healthOrganizations,
    handleManageOrganization,
    switchOrganizationStatus,
    handleSendPassword,
    handleViewDashboard,
    handleResendInvitation,
    isViewDashboardMode,
    parentHasActiveAdmin,
    onBlockActivation,
  ]);

  const columns = useMemo(() => {
    return healthOrganizations?.length > 0 ? renderColumns(searchValue, highlighted) : null;
  }, [healthOrganizations, highlighted, searchValue]);

  return (
    <>
    {alertModalVisible && EnterpriseAdminAlertModal}
    <EntitiesTableList
      title='Organizations'
      addNewBtnTitle='Add New Organization'
      Navigation={navigate}
      columns={columns}
      data={tableData}
      handleChangePage={handleChangePage}
      // showAddNewButton={isViewDashboardMode && parentHasActiveAdmin}
      handleChangeSearch={handleChangeSearch}
      searchValue={searchValue}
      limit={limit}
      currentPage={currentPage}
      total={total}
      totalPages={totalPages}
      showOrganizationStatus
      selectedOrganizationStatus={selectedOrganizationStatus}
      handleOrganizationStatus={handleOrganizationStatus}
    />
    </>
  );
};

const mapStateToProps = (state) => ({
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  limit: state.healthOrganizations.limit,
  currentPage: state.healthOrganizations.currentPage || 1,
  total: state.healthOrganizations.total,
  totalPages: state.healthOrganizations.totalPages,
  isViewDashboardMode: state?.authorization?.viewDashboardMode?.active,
  currentHealthOrganization: state.healthOrganizations.currentHealthOrganization,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchHealthOrganizations,
      disableHO,
      sendNewPassword,
      clearHOInfo,
      resendInvitationForUser,
      loginAsDifferentRole,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Child_Health_Organizations);
